import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Reset failed. Please request a new link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#2D1B36] flex items-center justify-center px-6" data-testid="reset-password-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm bg-[#FBF9F6] p-10"
      >
        <div className="flex items-center gap-3 mb-2">
          <KeyRound size={20} strokeWidth={1.5} className="text-[#C5A059]" />
          <h1 className="font-serif-d text-3xl text-[#2D1B36]">New Password</h1>
        </div>
        {!token ? (
          <p className="mt-6 text-sm text-[#514357]" data-testid="reset-no-token">
            This reset link is incomplete. Please request a fresh one from the sign-in page.
          </p>
        ) : done ? (
          <div data-testid="reset-success">
            <p className="mt-6 text-sm text-[#514357] leading-relaxed">
              Your password is updated. You can sign in with it now.
            </p>
            <button
              onClick={() => navigate("/auth")}
              data-testid="reset-goto-signin"
              className="mt-8 w-full bg-[#4A2B58] text-[#FBF9F6] font-accent text-xs tracking-[0.25em] uppercase py-4 hover:bg-[#2D1B36] transition-colors duration-300"
            >
              Go to Sign In
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-7" data-testid="reset-form">
            {[
              { label: "New Password", value: password, set: setPassword, testid: "reset-input-password" },
              { label: "Confirm Password", value: confirm, set: setConfirm, testid: "reset-input-confirm" },
            ].map((f) => (
              <div key={f.testid}>
                <label className="font-accent text-[10px] tracking-[0.3em] uppercase text-[#8A818F]">{f.label}</label>
                <input
                  required
                  type="password"
                  minLength={6}
                  placeholder="At least 6 characters"
                  value={f.value}
                  onChange={(e) => f.set(e.target.value)}
                  data-testid={f.testid}
                  className="w-full bg-transparent border-b border-[#E8E4DF] focus:border-[#C5A059] px-0 py-3 text-[#2D1B36] placeholder-[#8A818F] transition-colors duration-300"
                />
              </div>
            ))}
            {error && <p data-testid="reset-error" className="text-sm text-red-700">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit-button"
              className="w-full bg-[#4A2B58] text-[#FBF9F6] font-accent text-xs tracking-[0.25em] uppercase py-4 hover:bg-[#2D1B36] transition-colors duration-300 disabled:opacity-60"
            >
              {busy ? "Updating…" : "Update Password"}
            </button>
          </form>
        )}
        <Link
          to="/auth"
          data-testid="reset-back-link"
          className="block mt-6 text-center font-accent text-[10px] tracking-[0.25em] uppercase text-[#8A818F] hover:text-[#C5A059] transition-colors"
        >
          Back to sign in
        </Link>
      </motion.div>
    </div>
  );
}
